/*
  Wild Paws and Claws – product page helpers (loaded as a module by the wpc-product-* blocks/sections).
  - <wpc-gallery-nav>           desktop gallery: main image + thumbnail row, instant swap, hover preload
  - <wpc-variant-sync>          re-renders server-side snippets (stock line) when Horizon changes variant
  - <wpc-product-pairs>         "Pairs well with" shared product cards, rendered by sections/wpc-product-recommendations
                                through the Section Rendering API (complementary → related)
  - subscriptions widget        one "Subscribe & save" option + frequency <select> driving the app's own radios;
                                prices without the "GBP" suffix
  - <wpc-product-recommendations> "You may also like" grid via the Section Rendering API
  - sticky gallery offset       long galleries scroll to their end before sticking
  - sticky add-to-cart (<990px) sets html.wpc-sticky-atc-visible + --wpc-sticky-atc-height so floating
                                buttons (WhatsApp / Chaty) can sit above Horizon's sticky bar
  All parts are safe to load more than once and re-init on shopify:section:load.
*/
import { StandardEvents } from '@shopify/events';

/* Keep theme-preview params on same-origin fetches (myshopify.com previews otherwise redirect
   cross-origin to the primary domain and the request fails). No-op on the live storefront. */
function withPreviewParams(url) {
  const current = new URLSearchParams(location.search);
  ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
    if (current.has(key) && !url.searchParams.has(key)) url.searchParams.set(key, current.get(key));
  });
  return url;
}

/* ---------- Variant sync ---------- */
class WpcVariantSync extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    if (!this.section) return;
    this.onSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card, .wpc-product-card')) return;
      if (!event.promise) return;
      event.promise
        .then(({ detail } = {}) => {
          const html = detail && detail.html;
          if (!html) return;
          const fresh = html.querySelector(`wpc-variant-sync[data-key="${this.dataset.key}"]`);
          if (fresh) this.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    };
    this.section.addEventListener(StandardEvents.productSelect, this.onSelect);
  }
  disconnectedCallback() {
    if (this.section && this.onSelect) this.section.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }
}
if (!customElements.get('wpc-variant-sync')) customElements.define('wpc-variant-sync', WpcVariantSync);

/* ---------- Pairs well with ---------- */
class WpcProductPairs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    this.dataset.loaded = 'true';
    this.load();
  }

  /* Renders sections/wpc-product-recommendations for this product and returns its card <li>s. */
  async fetchCards(intent) {
    const url = new URL(this.dataset.url, location.origin);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);
    url.searchParams.set('section_id', this.dataset.sectionType || 'wpc-product-recommendations');
    withPreviewParams(url);
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) return [];
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    return Array.from(doc.querySelectorAll('.wpc-product-grid > li')).filter((li) => {
      const card = li.querySelector('.wpc-product-card');
      return (
        card && !card.classList.contains('wpc-product-card--soldout') && card.dataset.productId !== this.dataset.productId
      );
    });
  }

  async load() {
    const limit = Number(this.dataset.limit || 2);
    try {
      let cards = await this.fetchCards('complementary');
      if (cards.length === 0 && this.dataset.fallback !== 'false') cards = await this.fetchCards('related');
      cards = cards.slice(0, limit);
      if (!cards.length) return;
      this.querySelector('[data-wpc-pairs-list]').replaceChildren(...cards.map((li) => document.importNode(li, true)));
      this.hidden = false;
    } catch (error) {
      /* Recommendations are a nice-to-have: stay hidden on failure. */
    }
  }
}
if (!customElements.get('wpc-product-pairs')) customElements.define('wpc-product-pairs', WpcProductPairs);

/* ---------- Subscriptions app widget (progressive enhancement) ----------
   The Shopify Subscriptions app renders one <section> per variant with radios for one-time purchase and
   each delivery frequency. We hide the frequency radios and show one subscribe option (with a "Save X%" pill
   when the plan is discounted) plus a frequency <select>; choosing either clicks the app's real radio, so the app keeps setting selling_plan
   on the product form exactly as before. Without JS the app's own markup shows. */
const SUBS_BLOCK = '.shopify_subscriptions_app_block';

function stripCurrency(root) {
  root.querySelectorAll('.in_widget_price, .wpc-subs__price').forEach((el) => {
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    const clean = text.replace(/\s+[A-Z]{3}$/, '');
    if (clean !== text || el.textContent !== text) el.textContent = clean;
  });
}

function planLabel(radio) {
  const span = radio.closest('label')?.querySelector('.title_and_price_wrapper > span');
  return (span ? span.textContent : radio.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim();
}

/* Saving % only when the app applies a price adjustment and the plan price is below the one-time price. */
const moneyValue = (text) => parseFloat(String(text || '').replace(/[^\d.]/g, '')) || 0;
function planSaving(radio) {
  if (!radio || radio.dataset.sellingPlanAdjustment === '0') return 0;
  const price = moneyValue(radio.dataset.variantPrice);
  const compare = moneyValue(radio.dataset.variantCompareAtPrice);
  return price > 0 && compare > price ? Math.round(((compare - price) / compare) * 100) : 0;
}

/* Main product price follows the selected plan in the visible (current variant) app section. */
function syncMainPrice(container) {
  const bdi = container.closest('.shopify-section')?.querySelector('product-price .price bdi');
  if (!bdi) return;
  const section = Array.from(container.querySelectorAll(SUBS_BLOCK)).find((el) => el.getClientRects().length);
  const radio = section?.querySelector('input[data-radio-type="selling_plan"]:checked');
  if (bdi.dataset.wpcBase == null) bdi.dataset.wpcBase = bdi.textContent;
  const next = planSaving(radio) ? radio.dataset.variantPrice.replace(/\s+[A-Z]{3}$/, '').trim() : bdi.dataset.wpcBase;
  if (bdi.textContent !== next) bdi.textContent = next;
}

function frequencyLabel(label) {
  const text = label.replace(/,?\s*\d+(?:\.\d+)?%\s*off/i, '').replace(/^deliver\s+/i, '').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

let subsUid = 0;
function enhanceSubscriptionGroup(group) {
  if (group.dataset.wpcEnhanced) return;
  const radios = Array.from(group.querySelectorAll('input[type="radio"][data-radio-type="selling_plan"]'));
  const list = group.querySelector('.group_list');
  if (radios.length < 2 || !list) return;
  group.dataset.wpcEnhanced = 'true';

  const labels = radios.map(planLabel);
  const savings = radios.map(planSaving);
  const groupName = group.querySelector('.group_name')?.textContent.trim() || 'Subscribe and save';
  const title = savings.some(Boolean) ? 'Subscribe & save' : groupName.replace(/\band\b/i, '&');
  const priceOf = (radio) => radio?.closest('label')?.querySelector('.in_widget_price')?.textContent.trim() || '';
  const uid = `wpc-subs-${(subsUid += 1)}`;

  const wrap = document.createElement('div');
  wrap.className = 'wpc-subs';
  wrap.innerHTML = `
    <label class="wpc-subs__option custom_radio_button">
      <input type="radio" class="wpc-subs__proxy" name="${uid}">
      <span class="wpc-subs__title"></span>
      <span class="wpc-subs__save" hidden></span>
      <span class="wpc-subs__price"></span>
    </label>
    <div class="wpc-subs__freq">
      <label class="wpc-subs__freq-label" for="${uid}-freq">Delivery frequency</label>
      <div class="wpc-subs__select-wrap"><select class="wpc-subs__select" id="${uid}-freq"></select></div>
    </div>`;
  wrap.querySelector('.wpc-subs__title').textContent = title;
  const select = wrap.querySelector('select');
  radios.forEach((radio, i) => select.add(new Option(frequencyLabel(labels[i]), String(i))));
  const proxy = wrap.querySelector('.wpc-subs__proxy');
  const price = wrap.querySelector('.wpc-subs__price');
  const save = wrap.querySelector('.wpc-subs__save');
  const container = group.closest('.shopify_subscriptions_app_container');

  const sync = () => {
    const checked = radios.findIndex((r) => r.checked);
    proxy.checked = checked > -1;
    if (checked > -1) select.value = String(checked);
    const index = checked > -1 ? checked : Number(select.value);
    price.textContent = priceOf(radios[index]);
    save.hidden = !savings[index];
    save.textContent = savings[index] ? `Save ${savings[index]}%` : '';
    stripCurrency(wrap);
    if (container) syncMainPrice(container);
  };
  const choose = () => {
    const radio = radios[Number(select.value)];
    if (radio && !radio.checked) radio.click(); // the app's own change handler updates the form input
    sync();
  };
  proxy.addEventListener('change', choose);
  select.addEventListener('change', choose);
  group.closest(SUBS_BLOCK)?.addEventListener('change', (event) => {
    const t = event.target;
    if (t instanceof HTMLInputElement && t.type === 'radio' && t !== proxy) sync();
  });

  list.hidden = true;
  group.querySelector(':scope > .title_and_price_wrapper')?.setAttribute('hidden', '');
  list.after(wrap);
  sync();
}

function enhanceSubscriptions() {
  document.querySelectorAll(`${SUBS_BLOCK} .subscription_group`).forEach(enhanceSubscriptionGroup);
  document.querySelectorAll(SUBS_BLOCK).forEach(stripCurrency);
  document.querySelectorAll('.shopify_subscriptions_app_container').forEach(syncMainPrice);
}

let subsObserver;
function watchSubscriptions() {
  enhanceSubscriptions();
  if (subsObserver) subsObserver.disconnect();
  const blocks = document.querySelectorAll('.shopify-app-block:has(.shopify_subscriptions_app_container)');
  if (!blocks.length) return;
  let queued = false;
  subsObserver = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      enhanceSubscriptions();
    });
  });
  blocks.forEach((el) => {
    subsObserver.observe(el, { childList: true, subtree: true, characterData: true });
    // A variant change swaps the price container: re-apply the selected plan price
    const priceEl = el.closest('.shopify-section')?.querySelector('product-price');
    if (priceEl) subsObserver.observe(priceEl, { childList: true });
  });
}

/* ---------- You may also like ---------- */
class WpcProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('.wpc-product-grid') || this.dataset.loading) return;
    this.dataset.loading = 'true';
    fetch(withPreviewParams(new URL(this.dataset.url, location.origin)))
      .then((res) => (res.ok ? res.text() : ''))
      .then((text) => {
        if (!text) return;
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('wpc-product-recommendations');
        if (fresh && fresh.querySelector('.wpc-product-grid li')) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
        }
      })
      .catch(() => {})
      .finally(() => delete this.dataset.loading);
  }
}
if (!customElements.get('wpc-product-recommendations')) {
  customElements.define('wpc-product-recommendations', WpcProductRecommendations);
}

/* ---------- Desktop gallery: main image + thumbnail row ----------
   Rendered by snippets/product-media-gallery-content.liquid inside <media-gallery> (grid mode). Shows one grid
   item at a time ([data-wpc-current]) – an instant swap, no fade. Hovering a thumb/arrow preloads its image;
   the next image is preloaded once the page is idle. Horizon replaces the whole <media-gallery> on variant
   change (variant image first), so a fresh nav simply starts at index 0. Mobile keeps Horizon's slider. */
class WpcGalleryNav extends HTMLElement {
  connectedCallback() {
    const gallery = this.closest('media-gallery');
    const grid = gallery?.querySelector('.media-gallery__grid');
    if (!grid) return;
    this.items = [...grid.children];
    this.list = this.querySelector('.wpc-gallery-nav__thumbs');
    this.thumbs = [...this.querySelectorAll('[data-wpc-index]')];
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('click', (event) => {
      const target = this.targetIndex(event.target);
      if (target != null) this.go(target);
    }, { signal });
    this.addEventListener('pointerover', (event) => {
      const target = this.targetIndex(event.target);
      if (target != null) this.preload(target);
    }, { signal });
    this.list?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.go(this.current + (event.key === 'ArrowRight' ? 1 : -1));
      this.thumbs[this.current]?.focus();
    }, { signal });
    // Keep the main image in sync with the image last viewed in Horizon's zoom dialog.
    gallery.addEventListener('zoom-media:selected', (event) => {
      if (Number.isInteger(event.detail?.index)) this.go(event.detail.index);
    }, { signal });

    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 300));
    idle(() => this.preload(this.wrap(this.current + 1)));
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  get current() {
    return Math.max(0, this.items.findIndex((item) => item.hasAttribute('data-wpc-current')));
  }

  /* Index a thumb or arrow button points at (arrows wrap around). */
  targetIndex(node) {
    const button = node instanceof Element ? node.closest('button') : null;
    if (!button || !this.contains(button)) return null;
    if (button.dataset.wpcStep) return this.wrap(this.current + Number(button.dataset.wpcStep));
    if (button.dataset.wpcIndex) return Number(button.dataset.wpcIndex);
    return null;
  }

  wrap(index) {
    const count = this.items.length;
    return ((index % count) + count) % count;
  }

  /* Hidden items are lazy images that haven't loaded yet: switching them to eager fetches them now. */
  preload(index) {
    this.items[index]?.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
  }

  go(index) {
    const next = this.wrap(index);
    this.preload(next);
    this.items.forEach((item, i) => item.toggleAttribute('data-wpc-current', i === next));
    this.thumbs.forEach((thumb, i) => {
      if (i === next) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    // Scroll only the thumbnail row (never the page) so the active thumb is visible.
    const thumb = this.thumbs[next]?.parentElement;
    if (this.list && thumb) {
      const { scrollLeft, clientWidth } = this.list;
      if (thumb.offsetLeft < scrollLeft) this.list.scrollLeft = thumb.offsetLeft;
      else if (thumb.offsetLeft + thumb.offsetWidth > scrollLeft + clientWidth) {
        this.list.scrollLeft = thumb.offsetLeft + thumb.offsetWidth - clientWidth;
      }
    }
    this.preload(this.wrap(next + 1));
  }
}
if (!customElements.get('wpc-gallery-nav')) customElements.define('wpc-gallery-nav', WpcGalleryNav);

/* ---------- Page-level behaviour (runs once) ---------- */
if (!window.wpcProductInit) {
  window.wpcProductInit = true;
  const root = document.documentElement;
  const desktop = window.matchMedia('(min-width: 750px)');
  const stickyDesktop = window.matchMedia('(min-width: 990px)');

  /* Sticky gallery: a gallery taller than the viewport sticks by its bottom edge instead of its top,
     so every image can be seen while the buy column scrolls. */
  const galleryObserver = new ResizeObserver(() => updateGalleryTop());
  function updateGalleryTop() {
    document.querySelectorAll('.product-information:has([data-wpc-pdp]) media-gallery').forEach((gallery) => {
      if (!desktop.matches) {
        gallery.style.removeProperty('--wpc-gallery-top');
        return;
      }
      // Measure the header bar itself: it is what stays stuck once the announcement bar scrolls away
      // (Horizon's --header-height can still be its 60px fallback at this point).
      const headerBar = document.querySelector('#header-component');
      const header = headerBar
        ? headerBar.getBoundingClientRect().height
        : parseFloat(getComputedStyle(gallery).getPropertyValue('--header-height')) || 0;
      const offset = header + 24;
      const room = window.innerHeight - gallery.offsetHeight - 24;
      gallery.style.setProperty('--wpc-gallery-top', `${Math.min(offset, room)}px`);
    });
  }
  function watchGalleries() {
    document.querySelectorAll('.product-information:has([data-wpc-pdp]) media-gallery').forEach((g) => galleryObserver.observe(g));
    updateGalleryTop();
  }
  window.addEventListener('resize', updateGalleryTop, { passive: true });

  /* Mobile sticky add-to-cart (Horizon <sticky-add-to-cart>) → body class + height var for floating buttons. */
  let barObserver;
  function watchStickyBar() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (barObserver) barObserver.disconnect();
    if (!bar) {
      root.classList.remove('wpc-sticky-atc-visible');
      return;
    }
    const sync = () => {
      const visible = bar.dataset.stuck === 'true' && !stickyDesktop.matches && getComputedStyle(bar).display !== 'none';
      root.classList.toggle('wpc-sticky-atc-visible', visible);
      root.style.setProperty('--wpc-sticky-atc-height', visible ? `${Math.ceil(bar.getBoundingClientRect().height)}px` : '0px');
    };
    barObserver = new MutationObserver(sync);
    barObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
    sync();
  }

  const init = () => {
    watchGalleries();
    watchStickyBar();
    watchSubscriptions();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  desktop.addEventListener('change', init);
  stickyDesktop.addEventListener('change', init);
}
